import isEqual from "lodash/isEqual";
import { useCallback, useRef } from "react";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useRefreshPrintoutPresets } from "../../hooks/usePrintoutPresets";
import { resolveActivePreset } from "./detailedPresetState";
import type { PrintoutPresetsResponse } from "./printoutPresets";
import type { LivePresetState } from "./useLivePreset";

type Params = Pick<LivePresetState, "liveRef" | "replaceLive" | "sessionEditsRef"> & {
	/** Whether a refresh may run now: the list exists for this user and isn't already loading. */
	canRefresh: boolean;
};

/**
 * Asks the server again when the popup opens. The list is otherwise only reloaded by this user's own
 * writes, so a change made elsewhere — a support user saving the department's printouts as everyone's,
 * another window — would stay hidden until a page reload, and the next edit would hit a printout that no
 * longer exists. While the answer is on its way the cached list stays on screen.
 *
 * Never over this user's own writes (`useRefreshPrintoutPresets`): skipped while one is in flight, and its
 * answer dropped if one started or finished meanwhile — a save that landed, or a switch, is newer.
 *
 * When it answers:
 * - a different printout in use is picked up by `useLivePreset`'s seeding, like any switch;
 * - the same printout with different settings replaces what's on screen only if this session has no edits
 *   of its own for it — none waiting and none on their way — so nothing typed is ever clobbered. Resolves
 *   `true` then, so the caller can rebuild the preview.
 * The same state changes nothing: no new settings object, no remount, no preview rebuild.
 */
export const usePresetRefreshOnOpen = ({ liveRef, replaceLive, sessionEditsRef, canRefresh }: Params): (() => Promise<boolean>) => {
	const { chosenDepartment } = useChosenDepartment();
	const { trackError } = useAnalytics();
	const departmentId = chosenDepartment.department_id;
	const refreshPresets = useRefreshPrintoutPresets();
	const canRefreshRef = useRef<boolean>(canRefresh);
	canRefreshRef.current = canRefresh;

	return useCallback(async (): Promise<boolean> => {
		if (!canRefreshRef.current) return false;
		let answer: PrintoutPresetsResponse | null;
		try {
			answer = await refreshPresets();
		} catch (error) {
			// The cached list stays on screen; the failure is only worth knowing about, not interrupting for.
			trackError("printout_presets_refresh_on_open", { department_id: departmentId, error_message: error instanceof Error ? error.message : String(error) });
			return false;
		}
		if (!answer) return false;
		const fresh = resolveActivePreset(answer);
		const current = liveRef.current;
		if (!fresh || !current || current.presetId !== fresh.id) return false;
		if (sessionEditsRef.current.has(fresh.id) || isEqual(current.settings, fresh.settings)) return false;
		replaceLive({ presetId: fresh.id, settings: fresh.settings });
		return true;
	}, [refreshPresets, trackError, departmentId, liveRef, replaceLive, sessionEditsRef]);
};
